/**
 * useAudioGeneration - Delegates lifecycle to GenerationContext so that
 * progress + result survive tab switches and page reloads. The returned
 * shape is identical to the pre-context version; AudioGenerationPage
 * needs no changes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AudioGenerationRequest, AudioGenerationResponse, GenerationProgress } from '../types';
import { VideoGenerationAPI } from '../services/api';
import { audioNotification } from '../utils/audioNotification';
import { handleError } from '../utils/errorHandler';
import { useGeneration } from '../contexts/GenerationContext.context';
import { selectJobError, useRefreshResult } from './useGenerationJob';
import type { AudioJobResult } from '../services/polling/audioPollAdapter';
import type { SanitizedError } from '../utils/errorSanitizer';

interface UseAudioGenerationReturn {
  isGenerating: boolean;
  /** @deprecated Use progressData instead for full progress info including timing */
  progress: number;
  progressData: GenerationProgress | null;
  error: string | null;
  /**
   * Normalized, i18n-keyed error for display. When present, always render
   * this via t(sanitizedError.i18nKey) instead of the raw `error` string.
   */
  sanitizedError: SanitizedError | null;
  audioUrl: string | null;
  audioMetadata: AudioGenerationResponse | null;
  /**
   * Re-read the finished audio's job and return its result with freshly signed
   * links. `audioUrl` and the multilingual links expire an hour after the worker
   * signed them; anything about to hand one to the network asks for this first.
   * Rejects when the job can no longer be read.
   */
  refreshResult: () => Promise<AudioJobResult>;
  generateAudio: (config: AudioGenerationRequest) => Promise<void>;
  reset: () => void;
}

export const useAudioGeneration = (): UseAudioGenerationReturn => {
  const { startJob, failJob, clearJob, clearResults, getLatestActiveByTab, getLatestResultByTab } =
    useGeneration();
  const [startError, setStartError] = useState<string | null>(null);
  const lastNotifiedJobRef = useRef<string | null>(null);

  const activeJob = getLatestActiveByTab('audio');
  const resultJob = getLatestResultByTab('audio');

  // Prefer active job when there is one; otherwise fall back to the most
  // recent terminal job (so switching back to the tab shows its result/error).
  const current = activeJob ?? resultJob;
  const isGenerating =
    !!activeJob && (activeJob.status === 'queued' || activeJob.status === 'running');

  const progress = current?.progress ?? 0;
  const progressData: GenerationProgress | null = current
    ? {
        stage: current.stage || current.status,
        progress: current.progress ?? 0,
        message: current.message || 'Processing audio...',
        started_at: current.timing?.startedAt ?? null,
        completed_at: current.completedAt ? new Date(current.completedAt).toISOString() : null,
        elapsed_seconds: current.timing?.elapsedSeconds ?? null,
        estimated_remaining_seconds: current.timing?.estimatedRemainingSeconds ?? null,
      }
    : null;

  const completedResult =
    resultJob?.status === 'completed'
      ? (resultJob.result as AudioJobResult | undefined)
      : undefined;
  const audioUrl = completedResult?.audioUrl ?? null;
  const audioMetadata = completedResult?.metadata ?? null;
  const refreshResult = useRefreshResult<AudioJobResult>(resultJob);

  const jobError = selectJobError(activeJob, resultJob, isGenerating);

  const runtimeError: string | null = startError ?? jobError?.message ?? null;

  const sanitizedError: SanitizedError | null = jobError?.sanitized ?? null;

  // Notification side effects (play once per terminal transition)
  useEffect(() => {
    if (!resultJob) return;
    if (lastNotifiedJobRef.current === resultJob.id) return;
    lastNotifiedJobRef.current = resultJob.id;
    if (resultJob.status === 'completed') {
      audioNotification
        .playSuccess()
        .catch((err) => console.warn('[AudioGeneration] Audio notification failed:', err));
    } else if (resultJob.status === 'failed') {
      audioNotification
        .playError()
        .catch((err) => console.warn('[AudioGeneration] Audio notification failed:', err));
    }
  }, [resultJob]);

  const generateAudio = useCallback(
    async (config: AudioGenerationRequest): Promise<void> => {
      setStartError(null);
      try {
        const jobResponse = await VideoGenerationAPI.generateAudio(config);
        if (!jobResponse.success || !jobResponse.generation_id) {
          throw new Error(jobResponse.error || 'Failed to start audio generation');
        }
        startJob({
          id: jobResponse.generation_id,
          tabMode: 'audio',
          serverType: 'generation',
          metadata: config,
          title: 'Audio generation',
        });
      } catch (err: unknown) {
        const errorMessage = handleError(err, 'AudioGeneration', 'An error occurred');
        console.error('Audio generation error:', err);
        setStartError(errorMessage);
        audioNotification
          .playError()
          .catch((notifErr) =>
            console.warn('[AudioGeneration] Audio notification failed:', notifErr)
          );
      }
    },
    [startJob]
  );

  const reset = useCallback((): void => {
    setStartError(null);
    lastNotifiedJobRef.current = null;
    if (activeJob) {
      failJob(activeJob.id, { message: 'Cancelled by user', code: 'CANCELLED' }, 'cancelled');
      clearJob(activeJob.id);
    }
    // Every finished job of the tab, not just the latest: results stack per tab,
    // so clearing only the newest put the one before it back on screen -- a
    // failed run resurfaced as 'Generation Failed' after a successful one.
    clearResults('audio');
  }, [activeJob, failJob, clearJob, clearResults]);

  return {
    isGenerating,
    progress,
    progressData,
    error: runtimeError,
    sanitizedError,
    audioUrl,
    audioMetadata,
    refreshResult,
    generateAudio,
    reset,
  };
};
